import React, { useState, useEffect, useCallback, useRef } from 'react';
import './Orders.css';
import { toast } from 'react-toastify';
import { Search, X, Bike, ImageOff, Trash2, MapPin, Check, AlertTriangle } from 'lucide-react';
import { ordersAPI } from '../../config/orders-api';
import { driversAPI } from '../../config/drivers-api';
import { dispatchAPI } from '../../config/dispatch-api';
import { authAPI } from '../../config/auth-api';
import { helpers } from '../../utils/helpers';
import { isIndiaClient, hasStoreFeature } from '../../utils/client';
import { getDeviceId } from '../../utils/deviceId';
import { fetchAddressFromPincode, formatDateOnly, filterEnabledSlots, sortSlotsByTime } from '../../utils/utils';
import { formatOrderTotal, getSubTotalInputLabel, getDeliveryChargeInputLabel, getDiscountInputLabel, getTotalInputLabel, buildBillAmounts, computePayableTotal, appendNowTimeSlot, isNowTimeSlot } from '../../utils/orderDisplay';
import { resolveCoordinates, parseGoogleMapsLink, buildGoogleMapLink, isShortMapLink } from '../../utils/mapCoordinates';
import { formatPriorityBadgeLabel, normalizePriorityDelTime, splitPriorityDelTime, composePriorityDelTime, PRIORITY_MODES } from '../../utils/priorityDelivery';
import DriverSelectModal from '../Common/DriverSelectModal/DriverSelectModal';
import LocationPickerModal from '../Common/LocationPickerModal/LocationPickerModal';
import OrderDetailsModal from './OrderDetailsModal';
import { ADMIN_PERMISSIONS } from '../../constants/permissions';
import { settingsAPI } from '../../config/settings-api';
import {
  PAYMENT_TYPE_OPTIONS,
  formatPaymentType,
  getStatusMeta,
  buildStatusFilterOptions,
  getStatusBadgeStyles,
  getStatusButtonStyles,
  SHIPMENT_FIELD_KEYS,
  SHIPMENT_PARTNER_OPTIONS,
  formatShipmentPartner,
  isShippedStatus,
  requiresStatusReason,
  getVisibleOrderListColumns,
  migrateOrderListColumnsForRegion,
  getOrderListColumnLabel,
  getNextOrderStatus,
  getStatusStepperLabel,
  isNegativeOrderStatus,
  normalizeStatusValue,
  getStatusReasonField,
  normalizeOrderListColumns,
  DEFAULT_ORDER_LIST_COLUMNS,
  formatFieldLabel,
  formatOrderTypeLabel,
  getFlowStatusesForOrder,
} from '../../utils/orderConfigHelpers';
import { buildOrderStatusSnapshot, performOrderStatusUpdate } from '../../utils/orderStatusUpdate';
import ProductPickerModal from '../Common/ProductPickerModal/ProductPickerModal';
import PincodeSelectModal from '../Common/PincodeSelectModal/PincodeSelectModal';
import { normalizeOptionalWhatsapp, productsRequiredForStore } from '../../utils/orderContact';

const todayYmd = () => formatDateOnly(new Date());
const isMongoId = (value) => /^[a-fA-F0-9]{24}$/.test(String(value || '').trim());

const resolveListingStatusMeta = (order, flowOrderStatuses, flowNegativeStatuses) => {
  const orderType = String(order.type || 'order').toLowerCase();
  const status = normalizeStatusValue(order.orderStatus);
  const hasActiveReplacement = Boolean(order.activeReplacement);
  if (orderType === 'order' && hasActiveReplacement && ['delivered', 'replacement'].includes(status)) {
    return getStatusMeta('delivered', flowOrderStatuses, flowNegativeStatuses);
  }
  return getStatusMeta(order.orderStatus, flowOrderStatuses, flowNegativeStatuses);
};

const mergeSlotsWithSelection = (slots, timeSlotId, timeSlotName) => {
  const list = [...(slots || [])];
  const idStr = timeSlotId ? String(timeSlotId) : '';
  const nameStr = timeSlotName ? String(timeSlotName).trim() : '';
  if (!idStr && !nameStr) return list;

  const exists = list.some(
    (s) => String(s._id) === idStr
      || s.name === nameStr
      || s.name === idStr
      || String(s._id) === nameStr
  );

  if (!exists && nameStr) {
    list.unshift({
      _id: isMongoId(idStr) ? idStr : nameStr,
      name: nameStr,
      startTime: '',
    });
  }
  return list;
};

const cleanQatarPhone = (phone) => {
  if (!phone) return '';
  let cleaned = String(phone).replace(/\D/g, '');
  if (cleaned.startsWith('974')) {
    cleaned = cleaned.slice(3);
  } else if (cleaned.startsWith('00974')) {
    cleaned = cleaned.slice(5);
  }
  return cleaned.slice(0, 8);
};

const cleanIndiaPhone = (phone) => {
  if (!phone) return '';
  let cleaned = String(phone).replace(/\D/g, '');
  if (cleaned.startsWith('91')) {
    cleaned = cleaned.slice(2);
  } else if (cleaned.startsWith('0091')) {
    cleaned = cleaned.slice(4);
  } else if (cleaned.startsWith('0') && cleaned.length === 11) {
    cleaned = cleaned.slice(1);
  }
  return cleaned.slice(0, 10);
};

const emptyForm = {
  customerName: '',
  customerPhone: '',
  customerWhatsapp: '',
  zone: '',
  address: '',
  buildingNumber: '',
  apartment: '',
  directionToReach: '',
  pincode: '',
  state: '',
  district: '',
  country: '',
  city: '',
  driverId: '',
  totalPrice: '',
  deliveryCharge: '',
  discount: '',
  paymentType: '',
  advancePaidAmount: '',
  orderNote: '',
  deliveryDate: '',
  timeSlot: '',
  timeSlotId: '',
  lat: '',
  lng: '',
  mapLink: '',
  placeName: '',
  isPriorityDelivery: false,
  priority_del_time: '',
  priorityEndTime: '',
  priorityMode: 'before',
};

const Orders = ({ adminData, productFilter = null, onClearProductFilter }) => {
  const isIndia = isIndiaClient(adminData);
  const useCourierShipment = hasStoreFeature(adminData, 'courierShipment');
  const useDeliveryBoyShipment = hasStoreFeature(adminData, 'deliveryBoyShipment');
  const usePriorityDelivery = hasStoreFeature(adminData, 'priorityDelivery');

  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({
    deliveredOrders: 0,
    deliveredAmount: 0,
    returnedOrders: 0,
    returnedAmount: 0,
    replacementOrders: 0,
    replacementAmount: 0,
    inProgressOrders: 0,
    cancelledOrders: 0,
    cancelledAmount: 0,
    grossOrderAmount: 0,
    returnDeductionAmount: 0,
    totalOrderAmount: 0,
    totalDeliveryCharge: 0,
    totalDiscount: 0,
    totalProductsCount: 0,
  });
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({
    filterType: productFilter ? 'lifetime' : 'today',
    date: todayYmd(),
    month: (new Date().getMonth() + 1).toString(),
    year: new Date().getFullYear().toString(),
    startDate: '',
    endDate: '',
    status: '',
    orderType: 'all',
    driverId: 'All',
    courierPartner: 'All',
    timeSlot: 'All',
    adminId: 'All',
  });
  const [appliedFilters, setAppliedFilters] = useState(() => ({
    filterType: productFilter ? 'lifetime' : 'today',
    date: todayYmd(),
    month: (new Date().getMonth() + 1).toString(),
    year: new Date().getFullYear().toString(),
    startDate: '',
    endDate: '',
    status: '',
    orderType: 'all',
    driverId: 'All',
    courierPartner: 'All',
    timeSlot: 'All',
    adminId: 'All',
  }));
  const [activeProductFilter, setActiveProductFilter] = useState(productFilter);
  const [allTimeSlots, setAllTimeSlots] = useState([]);
  const [configTimeSlots, setConfigTimeSlots] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [modalSearchQuery, setModalSearchQuery] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    hasNext: false,
    hasPrev: false,
  });

  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [showDriverSelect, setShowDriverSelect] = useState(false);
  const [orderCustomFields, setOrderCustomFields] = useState([]);
  const [orderListColumns, setOrderListColumns] = useState(DEFAULT_ORDER_LIST_COLUMNS);
  const [orderStatuses, setOrderStatuses] = useState([]);
  const [negativeOrderStatuses, setNegativeOrderStatuses] = useState([]);
  const [statusUpdatingOrderId, setStatusUpdatingOrderId] = useState(null);
  const [listingStatusConfirm, setListingStatusConfirm] = useState({
    isOpen: false,
    order: null,
    status: null,
    title: '',
    message: '',
    reason: '',
  });
  const [listingShippedModal, setListingShippedModal] = useState({
    isOpen: false,
    order: null,
    shipmentNumber: '',
    shipmentPartner: '',
  });
  const [customFieldValues, setCustomFieldValues] = useState({});
  const [shipmentNumber, setShipmentNumber] = useState('');
  const [shipmentPartner, setShipmentPartner] = useState('');
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [isProductPickerOpen, setIsProductPickerOpen] = useState(false);
  const [isViewProductsOpen, setIsViewProductsOpen] = useState(false);
  const [pincodeOptions, setPincodeOptions] = useState([]);
  const [isPincodeModalOpen, setIsPincodeModalOpen] = useState(false);
  const [createSlots, setCreateSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [isLocationPickerOpen, setIsLocationPickerOpen] = useState(false);
  const [isLocationUpdated, setIsLocationUpdated] = useState(false);
  const [resolvingMapLink, setResolvingMapLink] = useState(false);
  const mapLinkResolveTimer = useRef(null);
  const [commentsModal, setCommentsModal] = useState({
    isOpen: false,
    order: null,
    comments: '',
  });

  const handleOpenCommentsModal = (order) => {
    setCommentsModal({
      isOpen: true,
      order,
      comments: order.comments || '',
    });
  };

  const handleCloseCommentsModal = () => {
    setCommentsModal({
      isOpen: false,
      order: null,
      comments: '',
    });
  };

  const handleSaveComments = async () => {
    if (!commentsModal.order) return;
    try {
      const res = await ordersAPI.updateOrder(commentsModal.order._id, {
        comments: commentsModal.comments,
      });
      if (res.success) {
        toast.success('Comments updated successfully');
        fetchOrders(currentPage, { silent: true });
        handleCloseCommentsModal();
      } else {
        toast.error(res.error || 'Failed to update comments');
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to update comments');
    }
  };
  const handleUpdateSelectedQty = (productId, variantSku, newQty) => {
    const qty = Math.max(1, parseInt(newQty) || 1);
    setSelectedProducts((prev) =>
      prev.map((item) => {
        const matchId = item._id === productId;
        const matchVariant = (item.selectedVariant?.sku || null) === (variantSku || null);
        return matchId && matchVariant ? { ...item, qty } : item;
      })
    );
  };

  const itemTotal = selectedProducts.reduce((sum, item) => sum + (item.price * item.qty), 0);

  useEffect(() => {
    if (selectedProducts.length > 0) {
      setFormData(prev => ({
        ...prev,
        totalPrice: itemTotal.toFixed(2)
      }));
    }
  }, [selectedProducts, itemTotal]);

  const canCreate = helpers.hasPermission(adminData, ADMIN_PERMISSIONS.CREATE_ORDERS);
  const canEdit = helpers.hasPermission(adminData, ADMIN_PERMISSIONS.EDIT_ORDERS);
  const canView = helpers.hasPermission(adminData, ADMIN_PERMISSIONS.VIEW_ORDERS);
  const canUpdateStatus = canEdit || helpers.hasPermission(adminData, ADMIN_PERMISSIONS.UPDATE_ORDER_STATUS);
  const canViewAllOrders = helpers.hasPermission(adminData, ADMIN_PERMISSIONS.VIEW_ALL_ORDERS);

  const fetchOrders = async (page = 1, { silent = false } = {}) => {
    try {
      if (!silent) {
        setLoading(true);
      }
      setError(null);

      const response = await ordersAPI.getOrdersList({
        page,
        limit: 100,
        status: appliedFilters.status || undefined,
        search: searchQuery || undefined,
        stats: true,
        filterType: appliedFilters.filterType,
        date: appliedFilters.filterType === 'date' ? appliedFilters.date : undefined,
        month: appliedFilters.filterType === 'month' ? appliedFilters.month : undefined,
        year: appliedFilters.filterType === 'month' ? appliedFilters.year : undefined,
        startDate: appliedFilters.filterType === 'range' ? appliedFilters.startDate : undefined,
        endDate: appliedFilters.filterType === 'range' ? appliedFilters.endDate : undefined,
        driverId: appliedFilters.driverId !== 'All' ? appliedFilters.driverId : undefined,
        courierPartner: appliedFilters.courierPartner !== 'All' ? appliedFilters.courierPartner : undefined,
        timeSlot: appliedFilters.timeSlot !== 'All' ? appliedFilters.timeSlot : undefined,
        adminId: appliedFilters.adminId !== 'All' ? appliedFilters.adminId : undefined,
        orderType: appliedFilters.orderType && appliedFilters.orderType !== 'all' ? appliedFilters.orderType : undefined,
        productId: activeProductFilter?.productId || undefined,
        productNumericId: activeProductFilter?.productNumericId || undefined,
      });

      if (response.success) {
        setOrders(response.data || []);
        setPagination(response.pagination || pagination);
        if (response.stats) setStats(response.stats);
        if (response.timeSlots && response.timeSlots.length > 0) {
          setAllTimeSlots(response.timeSlots.map((name) => ({ name })));
        }
      } else {
        setError(response.error || 'Failed to fetch orders');
      }
    } catch (err) {
      setError('Error fetching orders: ' + err.message);
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  const handleCloseOrderDetails = useCallback(() => {
    setSelectedOrderId(null);
  }, []);

  const handleOrderDetailsUpdated = useCallback(() => {
    fetchOrders(currentPage, { silent: true });
  }, [currentPage, appliedFilters, searchQuery]);

  useEffect(() => {
    if (!canView) return;
    fetchOrders(currentPage);
  }, [
    currentPage,
    appliedFilters,
    searchQuery,
    canView,
    activeProductFilter?.productId,
    activeProductFilter?.productNumericId,
  ]);

  useEffect(() => {
    if (!productFilter?.productId) {
      setActiveProductFilter(null);
      return;
    }

    setActiveProductFilter(productFilter);
    const lifetimeFilters = {
      filterType: 'lifetime',
      date: todayYmd(),
      month: (new Date().getMonth() + 1).toString(),
      year: new Date().getFullYear().toString(),
      startDate: '',
      endDate: '',
      status: '',
      orderType: 'all',
      driverId: 'All',
      courierPartner: 'All',
      timeSlot: 'All',
      adminId: 'All',
    };
    setFilters(lifetimeFilters);
    setAppliedFilters(lifetimeFilters);
    setCurrentPage(1);
    setSearchQuery('');
  }, [productFilter?.productId, productFilter?.productNumericId, productFilter?.productName]);

  useEffect(() => {
    const fetchTimeSlotsForFilter = async () => {
      try {
        const response = await ordersAPI.getOrdersList({
          page: 1,
          limit: 1,
          filterType: filters.filterType,
          date: filters.filterType === 'date' ? filters.date : undefined,
          month: filters.filterType === 'month' ? filters.month : undefined,
          year: filters.filterType === 'month' ? filters.year : undefined,
          startDate: filters.filterType === 'range' ? filters.startDate : undefined,
          endDate: filters.filterType === 'range' ? filters.endDate : undefined,
        });

        if (response.success && response.timeSlots && response.timeSlots.length > 0) {
          setAllTimeSlots(response.timeSlots.map((name) => ({ name })));
        }
      } catch (err) {
        console.error('Error fetching timeSlots for current date filter:', err);
      }
    };

    if (canView) {
      fetchTimeSlotsForFilter();
    }
  }, [filters.filterType, filters.date, filters.month, filters.year, filters.startDate, filters.endDate, canView]);

  useEffect(() => {
    if (canView) {
      driversAPI.getDriversList({ page: 1, limit: 100 }).then((res) => {
        if (res.success) setDrivers(res.data || []);
      });
      if (canViewAllOrders) {
        authAPI.getAdminsList(1, 100).then((res) => {
          if (res.success) setAdmins(res.data || []);
        });
      }
      settingsAPI.getGeneralConfig().then((res) => {
        if (res.success) {
          const statuses = res.data?.orderStatuses || [];
          setOrderCustomFields(res.data?.orderCustomFields || []);
          setOrderStatuses(statuses);
          setNegativeOrderStatuses(res.data?.negativeOrderStatuses || []);
          setOrderListColumns(normalizeOrderListColumns(res.data?.orderListColumns || []));
          setConfigTimeSlots(res.data?.defaultTimeSlots || []);
        }
      });
    }
  }, [canView, canViewAllOrders]);

  const handleFilterChange = (filterType, value) => {
    if (filterType === 'filterType') {
      setFilters(prev => {
        const newFilters = { ...prev, filterType: value };
        if (value === 'date') {
          newFilters.date = todayYmd();
        } else if (value === 'month') {
          newFilters.month = (new Date().getMonth() + 1).toString();
          newFilters.year = new Date().getFullYear().toString();
        } else if (value === 'range') {
          newFilters.startDate = '';
          newFilters.endDate = '';
        }
        return newFilters;
      });
    } else {
      setFilters(prev => ({ ...prev, [filterType]: value }));
    }
  };

  const handleApplyFilters = () => {
    if (filters.filterType === 'range' && (!filters.startDate || !filters.endDate)) {
      toast.error('Please select both start and end dates');
      return;
    }
    setCurrentPage(1);
    setSearchQuery('');
    setModalSearchQuery('');
    setAppliedFilters({ ...filters });
  };

  const handleGlobalSearch = () => {
    const query = modalSearchQuery.trim();
    setCurrentPage(1);
    setSearchQuery(query);
    setShowSearchModal(false);

    if (query) {
      const allTimeFilters = {
        filterType: 'all',
        date: '',
        month: '',
        year: '',
        startDate: '',
        endDate: '',
        status: '',
        orderType: 'all',
        driverId: 'All',
        courierPartner: 'All',
        timeSlot: 'All',
        adminId: 'All',
      };
      setFilters(allTimeFilters);
      setAppliedFilters(allTimeFilters);
    } else {
      const defaultFilters = {
        filterType: 'today',
        date: todayYmd(),
        month: (new Date().getMonth() + 1).toString(),
        year: new Date().getFullYear().toString(),
        startDate: '',
        endDate: '',
        status: '',
        orderType: 'all',
        driverId: 'All',
        courierPartner: 'All',
        timeSlot: 'All',
        adminId: 'All',
      };
      setFilters(defaultFilters);
      setAppliedFilters(defaultFilters);
    }
  };

  const handleResetSearch = () => {
    setModalSearchQuery('');
    setSearchQuery('');
    setCurrentPage(1);

    const defaultFilters = {
      filterType: 'today',
      date: todayYmd(),
      month: (new Date().getMonth() + 1).toString(),
      year: new Date().getFullYear().toString(),
      startDate: '',
      endDate: '',
      status: '',
      orderType: 'all',
      driverId: 'All',
      courierPartner: 'All',
      timeSlot: 'All',
      adminId: 'All',
    };
    setFilters(defaultFilters);
    setAppliedFilters(defaultFilters);
  };

  const fetchTimeSlots = useCallback(async (deliveryDate, selectedSlotId = '', selectedSlotName = '') => {
    if (!deliveryDate) {
      setCreateSlots([]);
      return;
    }
    setLoadingSlots(true);
    try {
      const result = await dispatchAPI.getDispatchDetails(deliveryDate);
      const apiSlots = result.success
        ? sortSlotsByTime(filterEnabledSlots(result.data?.timeSlots || []))
        : [];
      const withNow = appendNowTimeSlot(apiSlots, deliveryDate);
      setCreateSlots(mergeSlotsWithSelection(withNow, selectedSlotId, selectedSlotName));
    } catch {
      setCreateSlots(mergeSlotsWithSelection([], selectedSlotId, selectedSlotName));
    } finally {
      setLoadingSlots(false);
    }
  }, []);

  useEffect(() => {
    if (isCreateOpen && formData.deliveryDate) {
      fetchTimeSlots(formData.deliveryDate, formData.timeSlotId, formData.timeSlot);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreateOpen, formData.deliveryDate, fetchTimeSlots]);

  const handleCloseCreate = () => {
    if (mapLinkResolveTimer.current) {
      clearTimeout(mapLinkResolveTimer.current);
      mapLinkResolveTimer.current = null;
    }
    setResolvingMapLink(false);
    setIsCreateOpen(false);
    setFormData(emptyForm);
    setCustomFieldValues({});
    setShipmentNumber('');
    setShipmentPartner('');
    setSelectedProducts([]);
    setCreateSlots([]);
    setIsLocationUpdated(false);
  };

  const handleAddNew = () => {
    const today = todayYmd();
    setFormData({ ...emptyForm, deliveryDate: today });
    setCustomFieldValues({});
    setShipmentNumber('');
    setShipmentPartner('');
    setSelectedProducts([]);
    setIsCreateOpen(true);
    setIsLocationUpdated(false);
    fetchTimeSlots(today);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'paymentType') {
      setFormData((prev) => ({
        ...prev,
        paymentType: value,
        advancePaidAmount: value === 'advance_paid' ? prev.advancePaidAmount : '',
      }));
      return;
    }
    if (name === 'deliveryDate') {
      setFormData((prev) => ({ ...prev, deliveryDate: value, timeSlot: '', timeSlotId: '' }));
      fetchTimeSlots(value);
      return;
    }
    if (name === 'timeSlot') {
      const slot = createSlots.find((s) => s.name === value);
      setFormData((prev) => ({
        ...prev,
        timeSlot: value,
        timeSlotId: slot?._id && slot._id !== 'now' ? String(slot._id) : '',
      }));
      return;
    }
    if (name === 'customerPhone') {
      const cleaner = isIndia ? cleanIndiaPhone : cleanQatarPhone;
      setFormData((prev) => ({ ...prev, customerPhone: cleaner(value) }));
      return;
    }
    if (name === 'pincode') {
      const val = value.replace(/\D/g, '').slice(0, 6);
      setFormData((prev) => {
        const nextData = { ...prev, pincode: val };
        if (isIndia && val.length !== 6) {
          nextData.state = '';
          nextData.city = '';
          nextData.district = '';
        }
        return nextData;
      });

      if (isIndia && val.length === 6) {
        fetchAddressFromPincode(val)
          .then((addresses) => {
            if (addresses && addresses.length > 0) {
              if (addresses.length === 1) {
                setFormData((prev) => ({ ...prev, ...addresses[0] }));
                toast.success('Address details fetched from pincode');
              } else {
                setPincodeOptions(addresses);
                setIsPincodeModalOpen(true);
              }
            } else {
              toast.error('Invalid pincode or details not found');
            }
          })
          .catch((err) => {
            console.error('Error fetching pincode details', err);
            toast.error('Error fetching pincode details');
          });
      }
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const applyMapLinkValue = (rawLink) => {
    const mapLink = String(rawLink || '');
    const parsed = parseGoogleMapsLink(mapLink);

    setFormData((prev) => ({
      ...prev,
      mapLink,
      lat: parsed?.lat || '',
      lng: parsed?.lng || '',
    }));
    setIsLocationUpdated(false);

    if (mapLinkResolveTimer.current) {
      clearTimeout(mapLinkResolveTimer.current);
      mapLinkResolveTimer.current = null;
    }

    const trimmed = mapLink.trim();
    const needsResolve = !parsed && (
      isShortMapLink(trimmed)
      || /google\.[^/\s]+\/maps|maps\.google|goo\.gl/i.test(trimmed)
    );
    if (!needsResolve) return;

    mapLinkResolveTimer.current = setTimeout(async () => {
      setResolvingMapLink(true);
      try {
        const result = await ordersAPI.extractCoordinates(trimmed);
        if (result.success && result.coordinates) {
          const { lat, lng } = result.coordinates;
          setFormData((prev) => ({
            ...prev,
            lat: String(lat),
            lng: String(lng),
            mapLink: prev.mapLink,
          }));
          setIsLocationUpdated(true);
        } else {
          toast.error(result.error || 'Could not resolve this map link. Pick location on map.');
        }
      } catch {
        toast.error('Could not resolve this map link. Pick location on map.');
      } finally {
        setResolvingMapLink(false);
      }
    }, 400);
  };

  const handleMapLinkChange = (e) => {
    applyMapLinkValue(e.target.value);
  };

  const handleLocationPicked = (location) => {
    setFormData((prev) => ({
      ...prev,
      lat: String(location.lat),
      lng: String(location.lng),
      mapLink: location.mapLink || '',
      placeName: location.placeName || prev.placeName,
      address: location.placeName || prev.address,
    }));
    setIsLocationUpdated(true);
  };

  const handlePhoneBlur = (e) => {
    const value = e.target.value.trim();
    if (!value) {
      toast.error('Customer phone is required');
    } else if (isIndia) {
      if (value.length !== 10) toast.error('Phone number must be exactly 10 digits');
    } else if (!/^\d{8}$/.test(value)) {
      toast.error('Phone number must be exactly 8 digits');
    }
  };

  const handlePincodeBlur = (e) => {
    const value = e.target.value.trim();
    if (!value) {
      toast.error('Pincode is required');
    } else if (!/^\d{6}$/.test(value)) {
      toast.error('Pincode must be 6 digits');
    }
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (!formData.customerName.trim()) {
        toast.error('Customer name is required');
        return;
      }
      if (!formData.paymentType) {
        toast.error('Payment type is required');
        return;
      }
      if (formData.paymentType === 'advance_paid') {
        const advanceAmount = Number(formData.advancePaidAmount);
        if (!Number.isFinite(advanceAmount) || advanceAmount <= 0) {
          toast.error('Advance amount is required for Advance Payed orders');
          return;
        }
      }
      const localPhone = formData.customerPhone.trim();
      if (!localPhone) {
        toast.error('Customer phone is required');
        return;
      }
      if (isIndia) {
        if (localPhone.length !== 10) {
          toast.error('Phone number must be exactly 10 digits');
          return;
        }
        if (!/^\d{6}$/.test(formData.pincode.trim())) {
          toast.error('Pincode must be 6 digits');
          return;
        }
        if (!formData.state.trim()) {
          toast.error('State is required');
          return;
        }
        if (!formData.district.trim()) {
          toast.error('District is required');
          return;
        }
        if (!formData.city.trim()) {
          toast.error('City is required');
          return;
        }
      } else {
        if (!/^\d{8}$/.test(localPhone)) {
          toast.error('Phone number must be exactly 8 digits');
          return;
        }
        if (!formData.zone.trim()) {
          toast.error('Zone number is required');
          return;
        }
      }
      if (!formData.address.trim()) {
        toast.error('Address is required');
        return;
      }
      if (!formData.deliveryDate) {
        toast.error('Delivery date is required');
        return;
      }
      if (!formData.timeSlot) {
        toast.error('Time slot is required');
        return;
      }
      if (usePriorityDelivery && formData.isPriorityDelivery) {
        const priorityCheck = normalizePriorityDelTime(
          composePriorityDelTime(formData.priorityMode, formData.priority_del_time, formData.priorityEndTime),
          { enabled: true }
        );
        if (!priorityCheck.success) {
          toast.error(priorityCheck.msg || 'Select a priority delivery time');
          return;
        }
      }

      for (const field of orderCustomFields) {
        if (field.required && !String(customFieldValues[field.key] || '').trim()) {
          toast.error(`${field.label} is required`);
          return;
        }
      }

      if (productsRequiredForStore(adminData) && selectedProducts.length === 0) {
        toast.error('At least one product is required');
        return;
      }

      const coords = resolveCoordinates({
        lat: formData.lat,
        lng: formData.lng,
        mapLink: formData.mapLink.trim(),
      });

      const fullPhone = isIndia ? `+91${localPhone}` : `+974${localPhone}`;
      const selectedSlot = createSlots.find((s) => s.name === formData.timeSlot);
      const slotId = isMongoId(selectedSlot?._id)
        ? String(selectedSlot._id)
        : (isMongoId(formData.timeSlotId) ? formData.timeSlotId : null);

      const orderSubTotal = selectedProducts.length > 0
        ? itemTotal
        : (parseFloat(formData.totalPrice) || 0);
      const bill = isIndia
        ? { subTotal: orderSubTotal, totalPrice: orderSubTotal }
        : buildBillAmounts({
          subTotal: orderSubTotal,
          deliveryCharge: formData.deliveryCharge,
          discount: formData.discount,
        });

      const shipmentCustomFields = useCourierShipment
        ? {
          ...(shipmentNumber.trim() ? { [SHIPMENT_FIELD_KEYS.number]: shipmentNumber.trim() } : {}),
          ...(shipmentPartner ? { [SHIPMENT_FIELD_KEYS.partner]: shipmentPartner } : {}),
        }
        : useDeliveryBoyShipment
          ? { ...(shipmentPartner ? { [SHIPMENT_FIELD_KEYS.partner]: shipmentPartner } : {}) }
          : {};

      const result = await ordersAPI.createOrder({
        adminId: adminData?._id,
        deviceId: getDeviceId(),
        customerName: formData.customerName.trim(),
        customerPhone: fullPhone,
        receiversWhatsappNumber: normalizeOptionalWhatsapp(formData.customerWhatsapp),
        zone: isIndia ? '' : formData.zone.trim(),
        address: formData.address.trim(),
        buildingNumber: formData.buildingNumber.trim(),
        apartment: formData.apartment.trim(),
        directionToReach: formData.directionToReach.trim(),
        ...(isIndia ? {
          pincode: formData.pincode.trim(),
          state: formData.state.trim(),
          district: formData.district.trim(),
          country: formData.country.trim() || 'India',
          city: formData.city.trim(),
        } : {}),
        bill,
        products: selectedProducts.map(p => ({
          _id: p._id,
          productId: p.productId,
          name: p.name,
          price: parseFloat(p.price) || 0,
          qty: parseInt(p.qty) || 1,
          images: p.images || [],
          selectedVariant: p.selectedVariant || null,
        })),
        paymentType: formData.paymentType || '',
        advancePaidAmount: formData.paymentType === 'advance_paid'
          ? (parseFloat(formData.advancePaidAmount) || 0)
          : 0,
        orderNote: formData.orderNote.trim(),
        deliveryDate: formatDateOnly(formData.deliveryDate),
        timeSlot: formData.timeSlot,
        timeSlotId: isNowTimeSlot(formData.timeSlot) ? null : (slotId || null),
        ...(usePriorityDelivery ? {
        isPriorityDelivery: usePriorityDelivery && Boolean(formData.isPriorityDelivery),
        priority_del_time: usePriorityDelivery && formData.isPriorityDelivery
          ? composePriorityDelTime(formData.priorityMode, formData.priority_del_time, formData.priorityEndTime)
          : '',
        } : {}),
        lat: coords?.lat || '',
        lng: coords?.lng || '',
        mapLink: coords
          ? buildGoogleMapLink(coords.lat, coords.lng)
          : '',
        customFields: {
          ...customFieldValues,
          ...shipmentCustomFields,
        },
        ...(formData.driverId ? { driverId: formData.driverId } : {}),
      });

      if (result.success) {
        toast.success('Order created successfully');
        handleCloseCreate();
        fetchOrders(currentPage);
      } else {
        toast.error(result.error || 'Failed to create order');
      }
    } catch {
      toast.error('An error occurred while creating the order');
    } finally {
      setSubmitting(false);
    }
  };

  const renderDriverCell = (order) => {
    const name = order.driverId?.name;
    if (!name) return <span>-</span>;
    return (
      <div className="orderlist-staff-row orderlist-staff-row-delivery">
        <Bike size={14} />
        <span className="orderlist-staff-name">{name}</span>
      </div>
    );
  };

  const visibleColumns = getVisibleOrderListColumns(
    migrateOrderListColumnsForRegion(orderListColumns, isIndia)
  ).filter((column) => column.key !== 'admin' || canViewAllOrders);

  const getOrderCustomField = (order, key) => {
    const fields = order?.customFields;
    if (!fields) return '';
    if (typeof fields.get === 'function') return String(fields.get(key) || '').trim();
    return String(fields[key] || '').trim();
  };

  const executeListingStatusUpdate = async (order, statusValue, reason = '') => {
    if (!order?._id || !statusValue) return;

    setStatusUpdatingOrderId(order._id);
    const snapshot = buildOrderStatusSnapshot(order);
    const extras = {};
    const reasonField = getStatusReasonField(statusValue);
    if (reasonField) extras[reasonField] = reason;

    try {
      await performOrderStatusUpdate({
        orderId: order._id,
        newStatus: statusValue,
        snapshot,
        orderStatuses,
        negativeOrderStatuses,
        extras,
        onSuccess: () => {
          fetchOrders(currentPage, { silent: true });
        },
      });
    } finally {
      setStatusUpdatingOrderId(null);
    }
  };

  const handleListingStatusAdvance = async (order, nextStatus, isConfirmed = false, reason = '') => {
    if (!canUpdateStatus || !nextStatus || !order) return;

    const statusValue = normalizeStatusValue(nextStatus);

    if (!isConfirmed) {
      if (isShippedStatus(statusValue)) {
        setListingShippedModal({
          isOpen: true,
          order,
          shipmentNumber: getOrderCustomField(order, SHIPMENT_FIELD_KEYS.number) || '',
          shipmentPartner: getOrderCustomField(order, SHIPMENT_FIELD_KEYS.partner) || '',
        });
        return;
      }

      const meta = getStatusMeta(statusValue, orderStatuses, negativeOrderStatuses);
      if (requiresStatusReason(statusValue)) {
        setListingStatusConfirm({
          isOpen: true,
          order,
          status: statusValue,
          title: `Update to ${meta.label}`,
          message: `Add an optional reason for marking this order as ${meta.label.toLowerCase()}.`,
          reason: '',
        });
        return;
      }

      setListingStatusConfirm({
        isOpen: true,
        order,
        status: statusValue,
        title: `Update to ${meta.label}`,
        message: `Are you sure you want to mark this order as ${meta.label.toLowerCase()}?`,
        reason: '',
      });
      return;
    }

    setListingStatusConfirm({
      isOpen: false,
      order: null,
      status: null,
      title: '',
      message: '',
      reason: '',
    });
    await executeListingStatusUpdate(order, statusValue, reason);
  };

  const handleListingShippedSubmit = async () => {
    const { order, shipmentNumber, shipmentPartner } = listingShippedModal;
    if (!order?._id) return;

    const trimmedNumber = String(shipmentNumber || '').trim();
    let customFieldsPatch = {};

    if (useCourierShipment) {
      if (!trimmedNumber) {
        toast.error('Shipment number is required');
        return;
      }
      if (!shipmentPartner) {
        toast.error('Courier partner is required');
        return;
      }
      customFieldsPatch = {
        [SHIPMENT_FIELD_KEYS.number]: trimmedNumber,
        [SHIPMENT_FIELD_KEYS.partner]: shipmentPartner,
      };
    } else if (useDeliveryBoyShipment) {
      if (!shipmentPartner) {
        toast.error('Delivery boy is required');
        return;
      }
      customFieldsPatch = {
        [SHIPMENT_FIELD_KEYS.partner]: shipmentPartner,
      };
    }

    setStatusUpdatingOrderId(order._id);
    try {
      const updateResult = await ordersAPI.updateOrder(order._id, {
        customFields: {
          ...(order.customFields || {}),
          ...customFieldsPatch,
        },
      });
      if (!updateResult.success) {
        toast.error(updateResult.error || 'Failed to save shipment details');
        return;
      }

      const snapshot = buildOrderStatusSnapshot(order);
      await performOrderStatusUpdate({
        orderId: order._id,
        newStatus: 'shipped',
        snapshot,
        orderStatuses,
        negativeOrderStatuses,
        onSuccess: () => {
          setListingShippedModal({ isOpen: false, order: null, shipmentNumber: '', shipmentPartner: '' });
          fetchOrders(currentPage, { silent: true });
        },
      });
    } catch {
      toast.error('Failed to update order status');
    } finally {
      setStatusUpdatingOrderId(null);
    }
  };

  const renderOrderListCell = (column, order) => {
    const flow = getFlowStatusesForOrder(order, { orderStatuses, negativeOrderStatuses });
    const flowOrderStatuses = flow.orderStatuses;
    const flowNegativeStatuses = flow.negativeOrderStatuses;
    const statusMeta = resolveListingStatusMeta(order, flowOrderStatuses, flowNegativeStatuses);
    const listingReplacement = order.activeReplacement;

    switch (column.key) {
      case 'status':
        return (
          <div className="orderlist-status-badge-row">
            <span
              className="orderlist-status-badge"
              style={getStatusBadgeStyles(statusMeta.color)}
            >
              {statusMeta.label}
            </span>
            {listingReplacement && (
              <span className="orderlist-type-badge replacement">
                Replacement
              </span>
            )}
          </div>
        );
      case 'orderId':
        return (
          <span className="orderlist-id-with-type">
            <button
              type="button"
              className="orderlist-id-link"
              onClick={() => setSelectedOrderId(order._id)}
            >
              #{order.orderId}
            </button>
            <span className={`orderlist-type-badge${String(order.type || '').toLowerCase() === 'return' ? ' return' : ''}${String(order.type || '').toLowerCase() === 'replacement' ? ' replacement' : ''}`}>
              {formatOrderTypeLabel(order.type)}
            </span>
          </span>
        );
      case 'customer':
        return order.deliveryAddress?.receiversName || '-';
      case 'zone':
        return order.deliveryAddress?.zone || '-';
      case 'pincode':
        return order.deliveryAddress?.pincode || '-';
      case 'delivery':
        return (
          <div className="orderlist-delivery-cell-inner">
            <span>{order.deliveryDate ? new Date(order.deliveryDate).toLocaleDateString() : '-'}</span>
            {order.timeSlot && <small>{order.timeSlot}</small>}
            {formatPriorityBadgeLabel(order.priority_del_time) && (
              <span className="orderlist-priority-badge">
                {formatPriorityBadgeLabel(order.priority_del_time)}
              </span>
            )}
          </div>
        );
      case 'admin':
        return order.adminName || '-';
      case 'driver':
        return renderDriverCell(order);
      case 'total':
        return formatOrderTotal(order.bill?.totalPrice, adminData);
      case 'deliveryCharge':
        return formatOrderTotal(order.bill?.deliveryCharge, adminData);
      case 'discount':
        return formatOrderTotal(order.bill?.discount, adminData);
      case 'created':
        return order.createdAt ? new Date(order.createdAt).toLocaleDateString() : '-';
      case 'shipmentId':
        return getOrderCustomField(order, SHIPMENT_FIELD_KEYS.number) || '-';
      case 'courier': {
        const partner = getOrderCustomField(order, SHIPMENT_FIELD_KEYS.partner);
        if (!partner) return '-';
        const label = formatShipmentPartner(partner);
        return label === '---' ? '-' : label;
      }
      case 'orderButtons': {
        if (!canUpdateStatus) return '--';
        if (
          ['replacement_cancelled', 'cancelled'].includes(normalizeStatusValue(order.orderStatus))
          || isNegativeOrderStatus(order.orderStatus, flowNegativeStatuses)
        ) return '--';

        const nextPositiveStatus = getNextOrderStatus(order.orderStatus, flowOrderStatuses, flowNegativeStatuses);
        if (!nextPositiveStatus || isNegativeOrderStatus(nextPositiveStatus, flowNegativeStatuses)) return '--';

        const nextMeta = getStatusMeta(nextPositiveStatus, flowOrderStatuses, flowNegativeStatuses);
        const stepperLabel = isShippedStatus(nextPositiveStatus)
          ? `Mark ${nextMeta.label}`
          : getStatusStepperLabel(nextPositiveStatus, orderStatuses, negativeOrderStatuses);
        const isUpdating = statusUpdatingOrderId === order._id;

        return (
          <button
            type="button"
            className="orderlist-table-status-btn"
            style={getStatusButtonStyles(nextMeta.color)}
            disabled={isUpdating}
            onClick={(e) => {
              e.stopPropagation();
              handleListingStatusAdvance(order, nextPositiveStatus);
            }}
          >
            {isUpdating ? 'Updating...' : stepperLabel}
          </button>
        );
      }
      case 'customerPhone':
        return order.deliveryAddress?.receiversPhoneNUmber || '-';
      case 'address':
        return order.deliveryAddress?.address || '-';
      case 'buildingNumber':
        return order.deliveryAddress?.buildingNumber || '-';
      case 'apartment':
        return order.deliveryAddress?.apartment || '-';
      case 'directionToReach':
        return order.deliveryAddress?.directionToReach || '-';
      case 'state':
        return order.deliveryAddress?.state || '-';
      case 'district':
        return order.deliveryAddress?.district || '-';
      case 'country':
        return order.deliveryAddress?.country || '-';
      case 'city':
        return order.deliveryAddress?.city || '-';
      case 'paymentType':
        return formatPaymentType(order.paymentType);
      case 'itemTotal':
        return formatOrderTotal(order.bill?.itemTotal || order.bill?.subTotal, adminData);
      case 'productCount':
        return order.products?.length || 0;
      case 'orderNote':
        return order.orderNote?.trim() || order.notes?.trim() || '-';
      case 'returnReason':
        return order.cancelReason?.trim() || order.returnReason?.trim() || '-';
      case 'comments': {
        const commentVal = order.comments?.trim() || '';
        if (commentVal) {
          return <span>{commentVal}</span>;
        } else {
          return (
            <button
              type="button"
              className="orderlist-comments-add-btn"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenCommentsModal(order);
              }}
              style={{
                backgroundColor: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                padding: '4px 8px',
                cursor: 'pointer',
                fontSize: '12px'
              }}
            >
              Add Comment
            </button>
          );
        }
      }
      default:
        return getOrderCustomField(order, column.key) || '-';
    }
  };

  const getSelectedDriverName = (driverId) => {
    if (!driverId) return '';
    return drivers.find((d) => String(d._id) === String(driverId))?.name || '';
  };

  const handleCreateDriverSelect = (driver) => {
    if (!driver?._id) return;
    setFormData((prev) => ({ ...prev, driverId: driver._id }));
    setShowDriverSelect(false);
  };

  const statusFilterOptions = buildStatusFilterOptions(orderStatuses, negativeOrderStatuses);

  if (!canView) {
    return (
      <div className="orderlist-page">
        <div className="orderlist-empty-state"><p>You do not have permission to view orders.</p></div>
      </div>
    );
  }

  return (
    <div className="orderlist-page">
      <div className="orderlist-page-header">
        <div className="orderlist-page-title">
          <h1>
            {activeProductFilter?.productName
              ? `Orders of ${activeProductFilter.productName}`
              : 'Orders'}
          </h1>
          <p>
            {activeProductFilter?.productName
              ? 'Showing orders that include this product'
              : 'View and manage delivery orders'}
          </p>
          {activeProductFilter && (
            <button
              type="button"
              className="orderlist-clear-product-filter"
              onClick={() => {
                setActiveProductFilter(null);
                const defaultFilters = {
                  filterType: 'today',
                  date: todayYmd(),
                  month: (new Date().getMonth() + 1).toString(),
                  year: new Date().getFullYear().toString(),
                  startDate: '',
                  endDate: '',
                  status: '',
                  orderType: 'all',
                  driverId: 'All',
                  courierPartner: 'All',
                  timeSlot: 'All',
                  adminId: 'All',
                };
                setFilters(defaultFilters);
                setAppliedFilters(defaultFilters);
                setCurrentPage(1);
                onClearProductFilter?.();
              }}
            >
              Clear product filter
            </button>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button
            onClick={() => fetchOrders(currentPage, { silent: false })}
            className="orderlist-search-btn"
            style={{
              height: '38px',
              backgroundColor: '#3b82f6',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              borderRadius: '6px',
              padding: '0 20px',
              fontWeight: '600',
              border: 'none',
              fontSize: '14px'
            }}
            title="Refresh Orders"
            disabled={loading}
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
          {canCreate && (
            <button className="orderlist-create-btn" onClick={handleAddNew}>Create Order</button>
          )}
        </div>
      </div>

      <div className="orderlist-stats-grid">
        <div className="orderlist-stat-card">
          <h3>Delivered</h3>
          <p className="orderlist-stat-value">{stats.deliveredOrders ?? 0}</p>
          <p className="orderlist-stat-sub">{formatOrderTotal(stats.deliveredAmount, adminData)}</p>
        </div>
        <div className="orderlist-stat-card">
          <h3>Returned</h3>
          <p className="orderlist-stat-value">{stats.returnedOrders ?? 0}</p>
          <p className="orderlist-stat-sub">{formatOrderTotal(stats.returnedAmount, adminData)}</p>
        </div>
        <div className="orderlist-stat-card">
          <h3>Replacement</h3>
          <p className="orderlist-stat-value">{stats.replacementOrders ?? 0}</p>
          <p className="orderlist-stat-sub">{formatOrderTotal(stats.replacementAmount, adminData)}</p>
        </div>
        <div className="orderlist-stat-card">
          <h3>In Progress</h3>
          <p className="orderlist-stat-value">{stats.inProgressOrders ?? 0}</p>
        </div>
        <div className="orderlist-stat-card">
          <h3>Cancelled</h3>
          <p className="orderlist-stat-value">{stats.cancelledOrders ?? 0}</p>
          <p className="orderlist-stat-sub">{formatOrderTotal(stats.cancelledAmount, adminData)}</p>
        </div>
        <div className="orderlist-stat-card">
          <h3>Products Count</h3>
          <p className="orderlist-stat-value">{stats.totalProductsCount ?? 0}</p>
        </div>
        <div className="orderlist-stat-card">
          <h3>Returned amount</h3>
          <p className="orderlist-stat-value">{formatOrderTotal(stats.returnDeductionAmount, adminData)}</p>
        </div>
        <div className="orderlist-stat-card orderlist-stat-card-wide">
          <h3>Total Amount</h3>
          <p className="orderlist-stat-value">{formatOrderTotal(stats.totalOrderAmount, adminData)}</p>
          <p className="orderlist-stat-formula">
            {formatOrderTotal(stats.grossOrderAmount, adminData)}
            {' - '}
            {formatOrderTotal(stats.returnDeductionAmount, adminData)}
            {' (returned) = '}
            {formatOrderTotal(stats.totalOrderAmount, adminData)}
          </p>
        </div>
        <div className="orderlist-stat-card">
          <h3>Delivery Charge</h3>
          <p className="orderlist-stat-value">{formatOrderTotal(stats.totalDeliveryCharge, adminData)}</p>
        </div>
        <div className="orderlist-stat-card">
          <h3>Discount</h3>
          <p className="orderlist-stat-value">{formatOrderTotal(stats.totalDiscount, adminData)}</p>
        </div>
      </div>

      <div className="orderlist-filter-section-card">
        <div className="orderlist-filter-row">
          <div className="orderlist-filter-group">
            <label>Date</label>
            <select
              value={filters.filterType}
              onChange={(e) => handleFilterChange('filterType', e.target.value)}
              className="orderlist-filter-select"
            >
              <option value="today">Today</option>
              <option value="yesterday">Yesterday</option>
              <option value="date">Custom Date</option>
              <option value="month">Custom Month</option>
              <option value="range">Custom Range</option>
              <option value="lifetime">Lifetime</option>
            </select>
          </div>

          {filters.filterType === 'date' && (
            <div className="orderlist-filter-group">
              <label>Select Date</label>
              <input
                type="date"
                value={filters.date}
                onChange={(e) => handleFilterChange('date', e.target.value)}
                className="orderlist-filter-input"
              />
            </div>
          )}

          {filters.filterType === 'month' && (
            <>
              <div className="orderlist-filter-group">
                <label>Select Month</label>
                <select
                  value={filters.month}
                  onChange={(e) => handleFilterChange('month', e.target.value)}
                  className="orderlist-filter-select"
                >
                  <option value="1">January</option>
                  <option value="2">February</option>
                  <option value="3">March</option>
                  <option value="4">April</option>
                  <option value="5">May</option>
                  <option value="6">June</option>
                  <option value="7">July</option>
                  <option value="8">August</option>
                  <option value="9">September</option>
                  <option value="10">October</option>
                  <option value="11">November</option>
                  <option value="12">December</option>
                </select>
              </div>
              <div className="orderlist-filter-group">
                <label>Select Year</label>
                <select
                  value={filters.year}
                  onChange={(e) => handleFilterChange('year', e.target.value)}
                  className="orderlist-filter-select"
                >
                  {Array.from({ length: new Date().getFullYear() - 2022 + 1 }, (_, i) => new Date().getFullYear() - i).map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
            </>
          )}

          {filters.filterType === 'range' && (
            <>
              <div className="orderlist-filter-group">
                <label>Start Date</label>
                <input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => handleFilterChange('startDate', e.target.value)}
                  className="orderlist-filter-input"
                />
              </div>
              <div className="orderlist-filter-group">
                <label>End Date</label>
                <input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => handleFilterChange('endDate', e.target.value)}
                  className="orderlist-filter-input"
                />
              </div>
            </>
          )}

          <div className="orderlist-filter-group">
            <label>Status</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="orderlist-filter-select"
            >
              {statusFilterOptions.map((opt) => (
                <option key={opt.value || 'all'} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          <div className="orderlist-filter-group">
            <label>Order Type</label>
            <select
              value={filters.orderType || 'all'}
              onChange={(e) => handleFilterChange('orderType', e.target.value)}
              className="orderlist-filter-select"
            >
              <option value="all">All</option>
              <option value="order">Order</option>
              <option value="replacement">Replacement</option>
              <option value="return">Return</option>
            </select>
          </div>

          <div className="orderlist-filter-group">
            <label>Delivery Staff</label>
            <select
              value={filters.driverId}
              onChange={(e) => handleFilterChange('driverId', e.target.value)}
              className="orderlist-filter-select"
            >
              <option value="All">All Staff</option>
              {drivers.map((driver) => (
                <option key={driver._id} value={driver._id}>
                  {driver.name}
                </option>
              ))}
            </select>
          </div>

          {canViewAllOrders && (
            <div className="orderlist-filter-group">
              <label>Admin</label>
              <select
                value={filters.adminId || 'All'}
                onChange={(e) => handleFilterChange('adminId', e.target.value)}
                className="orderlist-filter-select"
              >
                <option value="All">All Admins</option>
                {admins.map((admin) => (
                  <option key={admin._id} value={admin._id}>
                    {admin.name || admin.email}
                  </option>
                ))}
              </select>
            </div>
          )}

          {(useCourierShipment || useDeliveryBoyShipment) && (
            <div className="orderlist-filter-group">
              <label>{useCourierShipment ? 'Courier Partner' : 'Delivery Boy'}</label>
              <select
                value={filters.courierPartner}
                onChange={(e) => handleFilterChange('courierPartner', e.target.value)}
                className="orderlist-filter-select"
              >
                <option value="All">{useCourierShipment ? 'All Partners' : 'All Delivery Boys'}</option>
                {useCourierShipment
                  ? SHIPMENT_PARTNER_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))
                  : drivers.map((driver) => (
                    <option key={driver._id} value={driver.name}>
                      {driver.name}
                    </option>
                  ))}
              </select>
            </div>
          )}

          <div className="orderlist-filter-group">
            <label>Time Slot</label>
            <select
              value={filters.timeSlot || 'All'}
              onChange={(e) => handleFilterChange('timeSlot', e.target.value)}
              className="orderlist-filter-select"
            >
              <option value="All">All Slots</option>
              {(allTimeSlots.length > 0 ? allTimeSlots : configTimeSlots).map((slot) => (
                <option key={slot._id || slot.name} value={slot.name}>
                  {slot.name}
                </option>
              ))}
            </select>
          </div>

          <div className="orderlist-filter-group">
            <label>&nbsp;</label>
            <button
              type="button"
              onClick={() => setShowSearchModal(true)}
              className="orderlist-pagination-button"
              style={{ height: '42px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', width: '100%' }}
            >
              <Search size={16} />
              Find Order
            </button>
          </div>

          <div className="orderlist-filter-group">
            <label>&nbsp;</label>
            <button
              type="button"
              onClick={handleApplyFilters}
              className="orderlist-search-btn"
              style={{ height: '42px', backgroundColor: '#3b82f6', color: 'white', width: '100%' }}
            >
              Apply Filters
            </button>
          </div>
        </div>
      </div>

      {searchQuery && (
        <div className="orderlist-search-row" style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
          <span style={{ fontSize: '14px', color: '#64748b' }}>
            Search result for: <strong>"{searchQuery}"</strong>
          </span>
          <button
            type="button"
            onClick={handleResetSearch}
            className="orderlist-reset-btn"
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '4px', border: 'none', background: 'none', cursor: 'pointer', color: '#ef4444' }}
            title="Clear Search"
          >
            <X size={16} />
          </button>
        </div>
      )}

      <div className="orderlist-table-container">
        {loading ? (
          <div className="orderlist-loading-state">
            <div className="orderlist-loading-spinner" />
            <p>Loading orders...</p>
          </div>
        ) : error ? (
          <div className="orderlist-error-state">
            <p>{error}</p>
            <button onClick={() => fetchOrders(currentPage)} className="orderlist-retry-button">Retry</button>
          </div>
        ) : orders.length === 0 ? (
          <div className="orderlist-empty-state"><p>No orders found</p></div>
        ) : (
          <>
            <div className="orderlist-table-wrapper">
              <table className="orderlist-table">
                <thead>
                  <tr>
                    {visibleColumns.map((column) => (
                      <th key={column.key}>{getOrderListColumnLabel(column, isIndia)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order) => (
                    <tr key={order._id}>
                      {visibleColumns.map((column) => (
                        <td key={`${order._id}-${column.key}`}>
                          {renderOrderListCell(column, order)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {pagination.totalPages > 1 && (
              <div className="orderlist-pagination">
                <button
                  onClick={() => setCurrentPage((p) => p - 1)}
                  disabled={!pagination.hasPrev || loading}
                  className="orderlist-pagination-button"
                >
                  Previous
                </button>
                <span className="orderlist-pagination-info">
                  Page {pagination.currentPage} of {pagination.totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage((p) => p + 1)}
                  disabled={!pagination.hasNext || loading}
                  className="orderlist-pagination-button"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {selectedOrderId && (
        <OrderDetailsModal
          orderId={selectedOrderId}
          adminData={adminData}
          canEdit={canEdit}
          canUpdateStatus={canUpdateStatus}
          onClose={handleCloseOrderDetails}
          onUpdated={handleOrderDetailsUpdated}
          onOpenRelatedOrder={(id) => setSelectedOrderId(id)}
        />
      )}

      {isCreateOpen && (
        <div className="orderlist-dialog-overlay">
          <div className="orderlist-dialog-box">
            <div className="orderlist-dialog-header">
              <h2>Create Order</h2>
              <button className="orderlist-close-btn" onClick={handleCloseCreate}>&times;</button>
            </div>
            <form onSubmit={handleCreateSubmit} className="orderlist-form">
              <div className="orderlist-form-grid">
                <div className="orderlist-form-group">
                  <label htmlFor="customerName">Customer Name *</label>
                  <input
                    type="text"
                    id="customerName"
                    name="customerName"
                    value={formData.customerName}
                    onChange={handleInputChange}
                    placeholder="Receiver name"
                    required
                  />
                </div>

                <div className="orderlist-form-group">
                  <label htmlFor="customerPhone">Customer Phone *</label>
                  <div className="orderlist-phone-input-wrapper">
                    <span className="orderlist-phone-prefix">{isIndia ? '+91' : '+974'}</span>
                    <input
                      type="tel"
                      id="customerPhone"
                      name="customerPhone"
                      value={formData.customerPhone}
                      onChange={handleInputChange}
                      onBlur={handlePhoneBlur}
                      placeholder="Phone number"
                      maxLength={isIndia ? 10 : 8}
                    />
                  </div>
                </div>

                <div className="orderlist-form-group">
                  <label htmlFor="customerWhatsapp">{formatFieldLabel('WhatsApp Number', false)}</label>
                  <input
                    type="text"
                    id="customerWhatsapp"
                    name="customerWhatsapp"
                    value={formData.customerWhatsapp}
                    onChange={handleInputChange}
                    placeholder="Optional — include country code if needed"
                    autoComplete="tel"
                  />
                </div>

                {isIndia && (
                  <>
                    <div className="orderlist-form-group">
                      <label htmlFor="pincode">Pincode *</label>
                      <input
                        type="text"
                        id="pincode"
                        name="pincode"
                        value={formData.pincode}
                        onChange={handleInputChange}
                        onBlur={handlePincodeBlur}
                        placeholder="6-digit pincode"
                        maxLength={6}
                        required
                      />
                    </div>
                    <div className="orderlist-form-group">
                      <label htmlFor="state">State *</label>
                      <input
                        type="text"
                        id="state"
                        name="state"
                        value={formData.state}
                        onChange={handleInputChange}
                        placeholder="State"
                        required
                      />
                    </div>
                    <div className="orderlist-form-group">
                      <label htmlFor="district">District *</label>
                      <input
                        type="text"
                        id="district"
                        name="district"
                        value={formData.district}
                        onChange={handleInputChange}
                        placeholder="District"
                        required
                      />
                    </div>
                    <div className="orderlist-form-group">
                      <label htmlFor="city">City *</label>
                      <input
                        type="text"
                        id="city"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        placeholder="City"
                        required
                      />
                    </div>
                  </>
                )}

                {!isIndia && (
                  <div className="orderlist-form-group">
                    <label htmlFor="zone">Zone Number *</label>
                    <input
                      type="text"
                      id="zone"
                      name="zone"
                      value={formData.zone}
                      onChange={handleInputChange}
                      placeholder="Zone Number"
                      required
                    />
                  </div>
                )}

                <div className="orderlist-form-group">
                  <label htmlFor="address">Address *</label>
                  <input
                    type="text"
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    placeholder="Street address"
                    required
                  />
                </div>

                <div className="orderlist-form-group">
                  <label htmlFor="buildingNumber">Building / House No. (optional)</label>
                  <input
                    type="text"
                    id="buildingNumber"
                    name="buildingNumber"
                    value={formData.buildingNumber}
                    onChange={handleInputChange}
                    placeholder="Building / House No."
                  />
                </div>

                {!isIndia && (
                  <div className="orderlist-form-group">
                    <label htmlFor="apartment">{formatFieldLabel('Apartment', false)}</label>
                    <input
                      type="text"
                      id="apartment"
                      name="apartment"
                      value={formData.apartment}
                      onChange={handleInputChange}
                      placeholder="Apartment"
                    />
                  </div>
                )}

                <div className="orderlist-form-group orderlist-form-full">
                  <label htmlFor="directionToReach">{formatFieldLabel('Directions', false)}</label>
                  <input
                    type="text"
                    id="directionToReach"
                    name="directionToReach"
                    value={formData.directionToReach}
                    onChange={handleInputChange}
                    placeholder="Directions to reach"
                  />
                </div>

                <div className="orderlist-form-group">
                  <label htmlFor="deliveryDate">Delivery Date *</label>
                  <input
                    type="date"
                    id="deliveryDate"
                    name="deliveryDate"
                    value={formData.deliveryDate}
                    min={todayYmd()}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="orderlist-form-group">
                  <label htmlFor="timeSlot">Time Slot *</label>
                  <select
                    id="timeSlot"
                    name="timeSlot"
                    value={formData.timeSlot}
                    onChange={handleInputChange}
                    required
                    disabled={!formData.deliveryDate || loadingSlots}
                  >
                    <option value="">
                      {loadingSlots
                        ? 'Loading slots...'
                        : createSlots.length
                          ? 'Select time slot'
                          : 'No time slots configured'}
                    </option>
                    {createSlots.map((slot) => (
                      <option key={slot._id || slot.name} value={slot.name}>{slot.name}</option>
                    ))}
                  </select>
                  {!loadingSlots && formData.deliveryDate && createSlots.length === 0 && (
                    <p className="orderlist-field-hint orderlist-field-hint-info">
                      No time slots configured. Add slots in admin settings.
                    </p>
                  )}
                </div>

                {usePriorityDelivery && (
                  <div className="orderlist-form-group orderlist-form-full orderlist-priority-delivery">
                    <label className="orderlist-checkbox-label" htmlFor="isPriorityDelivery">
                      <input
                        type="checkbox"
                        id="isPriorityDelivery"
                        name="isPriorityDelivery"
                        checked={Boolean(formData.isPriorityDelivery)}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setFormData((prev) => ({
                            ...prev,
                            isPriorityDelivery: checked,
                            priority_del_time: checked ? prev.priority_del_time : '',
                            priorityEndTime: checked ? prev.priorityEndTime : '',
                          }));
                        }}
                      />
                      Is priority delivery
                    </label>
                      {formData.isPriorityDelivery && (
                      <div className="orderlist-priority-time-row">
                        <label htmlFor="priorityMode">Priority time *</label>
                        <select
                          id="priorityMode"
                          value={PRIORITY_MODES.some((item) => item.value === formData.priorityMode) ? formData.priorityMode : 'before'}
                          onChange={(e) => setFormData((prev) => ({ ...prev, priorityMode: e.target.value }))}
                        >
                          {PRIORITY_MODES.map((item) => (
                            <option key={item.value} value={item.value}>{item.label}</option>
                          ))}
                        </select>
                        <div className="orderlist-priority-times">
                          <input
                            type="time"
                            id="priority_del_time"
                            name="priority_del_time"
                            value={splitPriorityDelTime(formData.priority_del_time).time || formData.priority_del_time || ''}
                            onChange={handleInputChange}
                            required
                          />
                          {formData.priorityMode === 'between' && (
                            <>
                              <span>to</span>
                              <input
                                type="time"
                                id="priorityEndTime"
                                name="priorityEndTime"
                                value={formData.priorityEndTime || ''}
                                onChange={handleInputChange}
                                required
                              />
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="orderlist-form-group orderlist-form-full">
                  <label htmlFor="mapLink">{formatFieldLabel('Delivery Location / Map Link', false)}</label>
                  <div className="orderlist-location-row-inline">
                    <button
                      type="button"
                      className="orderlist-map-btn"
                      onClick={() => setIsLocationPickerOpen(true)}
                    >
                      <MapPin size={16} /> Pick on Map
                    </button>
                    <input
                      type="text"
                      id="mapLink"
                      name="mapLink"
                      value={formData.mapLink}
                      onChange={handleMapLinkChange}
                      placeholder="Paste a Google Maps link"
                      className="orderlist-map-link-input"
                    />
                  </div>
                  {(formData.lat && formData.lng) ? (
                    <div className="orderlist-coords-below">
                      <Check className="orderlist-coords-success-icon" size={15} aria-hidden />
                      <span>
                        {isLocationUpdated ? 'Coordinates updated: ' : 'Coordinates fetched successfully: '}
                        {formData.lat}, {formData.lng}
                      </span>
                    </div>
                  ) : resolvingMapLink ? (
                    <div className="orderlist-coords-below">
                      <span>Resolving map link...</span>
                    </div>
                  ) : formData.mapLink.trim() ? (
                    <div className="orderlist-coords-below orderlist-coords-warning">
                      <AlertTriangle className="orderlist-coords-warning-icon" size={15} aria-hidden />
                      <span>Cannot parse coordinates from this link. Please pick location on map.</span>
                    </div>
                  ) : null}
                </div>

                {useCourierShipment && (
                  <>
                    <div className="orderlist-form-group">
                      <label htmlFor="shipmentNumber">{formatFieldLabel('Shipment Number', false)}</label>
                      <input
                        type="text"
                        id="shipmentNumber"
                        value={shipmentNumber}
                        onChange={(e) => setShipmentNumber(e.target.value)}
                        placeholder="Tracking / AWB number"
                      />
                    </div>
                    <div className="orderlist-form-group">
                      <label htmlFor="shipmentPartner">{formatFieldLabel('Courier Partner', false)}</label>
                      <select
                        id="shipmentPartner"
                        value={shipmentPartner}
                        onChange={(e) => setShipmentPartner(e.target.value)}
                      >
                        <option value="">Select partner</option>
                        {SHIPMENT_PARTNER_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                  </>
                )}
                {!useCourierShipment && useDeliveryBoyShipment && (
                  <div className="orderlist-form-group">
                    <label htmlFor="shipmentPartner">{formatFieldLabel('Delivery Boy', false)}</label>
                    <select
                      id="shipmentPartner"
                      value={shipmentPartner}
                      onChange={(e) => setShipmentPartner(e.target.value)}
                    >
                      <option value="">Select delivery boy</option>
                      {drivers.map((driver) => (
                        <option key={driver._id} value={driver.name}>{driver.name}</option>
                      ))}
                    </select>
                  </div>
                )}

                {orderCustomFields.map((field) => (
                  <div className="orderlist-form-group" key={field.key}>
                    <label htmlFor={`custom-${field.key}`}>
                      {formatFieldLabel(field.label, field.required)}
                    </label>
                    <input
                      id={`custom-${field.key}`}
                      value={customFieldValues[field.key] || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        setCustomFieldValues((prev) => ({ ...prev, [field.key]: value }));
                      }}
                      required={field.required}
                    />
                  </div>
                ))}

                <div className="orderlist-form-group orderlist-form-group-full">
                  <label htmlFor="orderNote">{formatFieldLabel('Order Note', false)}</label>
                  <textarea
                    id="orderNote"
                    name="orderNote"
                    value={formData.orderNote}
                    onChange={handleInputChange}
                    rows={2}
                    placeholder="Add a note for this order"
                  />
                </div>

                <div className="orderlist-form-group orderlist-form-full orderlist-products-section">
                  <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>{formatFieldLabel('Products', productsRequiredForStore(adminData))}</span>
                    <button
                      type="button"
                      className="orderlist-add-products-trigger-btn"
                      onClick={() => setIsProductPickerOpen(true)}
                    >
                      Add
                    </button>
                  </label>
                  {selectedProducts.length > 0 ? (
                    <div className="orderlist-selected-products-summary-card">
                      <div className="orderlist-selected-products-summary-text">
                        {selectedProducts.length} {selectedProducts.length === 1 ? 'product' : 'products'} selected
                      </div>
                      <button
                        type="button"
                        className="orderlist-view-products-toggle-btn"
                        onClick={() => setIsViewProductsOpen(true)}
                      >
                        View Products
                      </button>
                    </div>
                  ) : (
                    <div className="orderlist-no-products">No products selected yet.</div>
                  )}
                </div>

                <div className="orderlist-form-group">
                  <label htmlFor="itemTotal">Item Total</label>
                  <input
                    type="text"
                    id="itemTotal"
                    value={itemTotal.toFixed(2)}
                    disabled
                    readOnly
                  />
                </div>

                <div className="orderlist-form-group">
                  {/* Layout spacer */}
                </div>

                <div className="orderlist-form-group">
                  <label htmlFor="paymentType">Payment Type *</label>
                  <select
                    id="paymentType"
                    name="paymentType"
                    value={formData.paymentType}
                    onChange={handleInputChange}
                    required
                  >
                    {PAYMENT_TYPE_OPTIONS.map((opt) => (
                      <option key={opt.value || 'empty'} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                {formData.paymentType === 'advance_paid' && (
                  <div className="orderlist-form-group">
                    <label htmlFor="advancePaidAmount">Advance Amount *</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0.01"
                      id="advancePaidAmount"
                      name="advancePaidAmount"
                      value={formData.advancePaidAmount}
                      onChange={handleInputChange}
                      placeholder="Advance amount paid"
                      required
                    />
                  </div>
                )}

                {isIndia ? (
                  <div className="orderlist-form-group">
                    <label htmlFor="totalPrice">{formatFieldLabel(getTotalInputLabel(adminData), false)}</label>
                    <input
                      type="number"
                      step="0.01"
                      id="totalPrice"
                      name="totalPrice"
                      value={formData.totalPrice}
                      onChange={handleInputChange}
                      placeholder="Total amount"
                    />
                  </div>
                ) : (
                  <>
                    <div className="orderlist-form-group">
                      <label htmlFor="totalPrice">{formatFieldLabel(getSubTotalInputLabel(adminData), false)}</label>
                      <input
                        type="number"
                        step="0.01"
                        id="totalPrice"
                        name="totalPrice"
                        value={formData.totalPrice}
                        onChange={handleInputChange}
                        placeholder="Sub total"
                      />
                    </div>
                    <div className="orderlist-form-group">
                      <label htmlFor="deliveryCharge">{formatFieldLabel(getDeliveryChargeInputLabel(adminData), false)}</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        id="deliveryCharge"
                        name="deliveryCharge"
                        value={formData.deliveryCharge}
                        onChange={handleInputChange}
                        placeholder="Optional"
                      />
                    </div>
                    <div className="orderlist-form-group">
                      <label htmlFor="discount">{formatFieldLabel(getDiscountInputLabel(adminData), false)}</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        id="discount"
                        name="discount"
                        value={formData.discount}
                        onChange={handleInputChange}
                        placeholder="Optional"
                      />
                    </div>
                    <div className="orderlist-form-group orderlist-form-full">
                      <p className="orderlist-payable-total">
                        Total Amount:{' '}
                        <strong>
                          {formatOrderTotal(
                            computePayableTotal({
                              subTotal: selectedProducts.length > 0
                                ? itemTotal
                                : (parseFloat(formData.totalPrice) || 0),
                              deliveryCharge: formData.deliveryCharge,
                              discount: formData.discount,
                            }),
                            adminData
                          )}
                        </strong>
                      </p>
                    </div>
                  </>
                )}
              </div>

              <div className="orderlist-form-actions">
                <button type="button" className="orderlist-cancel-btn" onClick={handleCloseCreate} disabled={submitting}>
                  Cancel
                </button>
                <button type="submit" className="orderlist-submit-btn" disabled={submitting}>
                  {submitting ? 'Creating...' : 'Create Order'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ProductPickerModal
        isOpen={isProductPickerOpen}
        onClose={() => setIsProductPickerOpen(false)}
        onSave={setSelectedProducts}
        initialSelected={selectedProducts}
        adminData={adminData}
      />

      {isViewProductsOpen && (
        <div className="orderlist-sel-modal-overlay" onClick={() => setIsViewProductsOpen(false)}>
          <div className="orderlist-sel-products-modal" onClick={(e) => e.stopPropagation()}>
            <div className="orderlist-sel-modal-header">
              <h2 className="orderlist-sel-modal-header-title">Selected Products</h2>
              <button
                type="button"
                className="orderlist-sel-modal-close-x"
                onClick={() => setIsViewProductsOpen(false)}
              >
                &times;
              </button>
            </div>

            <div className="orderlist-sel-modal-body">
              {selectedProducts.length > 0 ? (
                <div className="orderlist-selected-products-table-wrapper" style={{ maxHeight: 'none', margin: 0 }}>
                  <table className="orderlist-selected-products-table">
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th>Price</th>
                        <th>Qty</th>
                        <th>Total</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedProducts.map((p, idx) => (
                        <tr key={`${p._id || idx}-${p.selectedVariant?.sku || ''}`}>
                          <td>
                            <div className="orderlist-table-product-info">
                              {p.images?.[0]?.thumbnail || p.images?.[0]?.image ? (
                                <img
                                  src={p.images?.[0]?.thumbnail || p.images?.[0]?.image}
                                  alt={p.name}
                                  className="orderlist-table-product-thumb"
                                />
                              ) : (
                                <div className="orderlist-table-product-thumb-fallback">
                                  <ImageOff size={14} />
                                </div>
                              )}
                              <div className="orderlist-table-product-name-wrapper">
                                <span className="orderlist-table-product-name">{p.name}</span>
                              </div>
                            </div>
                          </td>
                          <td>{formatOrderTotal(p.price, adminData)}</td>
                          <td>
                            <div className="orderlist-table-qty-selector">
                              <button
                                type="button"
                                className="orderlist-table-qty-btn"
                                onClick={() => handleUpdateSelectedQty(p._id, p.selectedVariant?.sku, p.qty - 1)}
                              >
                                -
                              </button>
                              <input
                                type="number"
                                min="1"
                                value={p.qty}
                                onChange={(e) => handleUpdateSelectedQty(p._id, p.selectedVariant?.sku, parseInt(e.target.value) || 1)}
                                className="orderlist-table-qty-input"
                              />
                              <button
                                type="button"
                                className="orderlist-table-qty-btn"
                                onClick={() => handleUpdateSelectedQty(p._id, p.selectedVariant?.sku, p.qty + 1)}
                              >
                                +
                              </button>
                            </div>
                          </td>
                          <td>{formatOrderTotal(p.qty * p.price, adminData)}</td>
                          <td>
                            <button
                              type="button"
                              className="orderlist-remove-product-btn"
                              onClick={() => setSelectedProducts(prev => prev.filter(item => !(item._id === p._id && (item.selectedVariant?.sku || null) === (p.selectedVariant?.sku || null))))}
                            >
                              <Trash2 size={15} />
                            </button>
                          </td>
                        </tr>
                      ))}                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="orderlist-no-products" style={{ margin: 0 }}>No products selected yet.</div>
              )}
            </div>

            <div className="orderlist-sel-modal-footer">
              <div style={{ fontWeight: '600', color: '#1e293b' }}>
                Total: {formatOrderTotal(itemTotal, adminData)}
              </div>
            </div>
          </div>
        </div>
      )}

      <DriverSelectModal
        isOpen={showDriverSelect}
        onClose={() => setShowDriverSelect(false)}
        drivers={drivers}
        selectedDriverId={formData.driverId}
        onSelect={handleCreateDriverSelect}
        title="Assign Driver"
      />

      {showSearchModal && (
        <div className="orderlist-dialog-overlay" onClick={() => setShowSearchModal(false)}>
          <div className="orderlist-dialog-box" style={{ maxWidth: '500px', width: '100%' }} onClick={(e) => e.stopPropagation()}>
            <div className="orderlist-dialog-header">
              <h2>Find Order</h2>
              <button className="orderlist-close-btn" onClick={() => setShowSearchModal(false)}>&times;</button>
            </div>
            <div className="orderlist-form">
              <div className="orderlist-form-group" style={{ marginBottom: '24px' }}>
                <input
                  type="text"
                  placeholder="Enter Order ID, customer, phone..."
                  value={modalSearchQuery}
                  onChange={(e) => setModalSearchQuery(e.target.value)}
                  autoFocus
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #cbd5e1',
                    borderRadius: '8px',
                    fontSize: '15px',
                    outline: 'none'
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleGlobalSearch();
                  }}
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                <button
                  type="button"
                  className="orderlist-pagination-button"
                  onClick={() => setShowSearchModal(false)}
                  style={{ padding: '10px 20px', background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="orderlist-search-btn"
                  onClick={handleGlobalSearch}
                  style={{ padding: '10px 28px', backgroundColor: '#3b82f6', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}
                >
                  Search
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <PincodeSelectModal
        isOpen={isPincodeModalOpen}
        options={pincodeOptions}
        onClose={() => setIsPincodeModalOpen(false)}
        onSelect={(addr) => {
          setFormData((prev) => ({ ...prev, ...addr }));
          setIsPincodeModalOpen(false);
        }}
      />

      {isLocationPickerOpen && (
        <LocationPickerModal
          onClose={() => setIsLocationPickerOpen(false)}
          isIndia={isIndia}
          initial={{ lat: formData.lat, lng: formData.lng, placeName: formData.placeName }}
          onConfirm={handleLocationPicked}
        />
      )}

      {listingShippedModal.isOpen && (
        <div
          className="orderlist-confirm-overlay"
          onClick={(e) => {
            e.stopPropagation();
            if (!statusUpdatingOrderId) {
              setListingShippedModal({ isOpen: false, order: null, shipmentNumber: '', shipmentPartner: '' });
            }
          }}
        >
          <div className="orderlist-confirm-dialog orderlist-shipment-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="orderlist-confirm-header">
              <h3>Mark as Shipped</h3>
              <button
                type="button"
                className="orderlist-confirm-close-btn"
                onClick={() => setListingShippedModal({ isOpen: false, order: null, shipmentNumber: '', shipmentPartner: '' })}
                disabled={!!statusUpdatingOrderId}
              >
                <X size={18} />
              </button>
            </div>
            <div className="orderlist-section-edit">
              {useCourierShipment && (
                <>
                  <div className="orderlist-form-group">
                    <label htmlFor="listing-shipment-number">Shipment Number *</label>
                    <input
                      id="listing-shipment-number"
                      value={listingShippedModal.shipmentNumber}
                      onChange={(e) => setListingShippedModal((prev) => ({ ...prev, shipmentNumber: e.target.value }))}
                      placeholder="Enter shipment tracking number"
                      disabled={!!statusUpdatingOrderId}
                    />
                  </div>
                  <div className="orderlist-form-group">
                    <label htmlFor="listing-shipment-partner">Courier Partner *</label>
                    <select
                      id="listing-shipment-partner"
                      value={listingShippedModal.shipmentPartner}
                      onChange={(e) => setListingShippedModal((prev) => ({ ...prev, shipmentPartner: e.target.value }))}
                      disabled={!!statusUpdatingOrderId}
                    >
                      <option value="">Select partner</option>
                      {SHIPMENT_PARTNER_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                </>
              )}
              {!useCourierShipment && useDeliveryBoyShipment && (
                <div className="orderlist-form-group">
                  <label htmlFor="listing-shipment-partner">Delivery Boy *</label>
                  <select
                    id="listing-shipment-partner"
                    value={listingShippedModal.shipmentPartner}
                    onChange={(e) => setListingShippedModal((prev) => ({ ...prev, shipmentPartner: e.target.value }))}
                    disabled={!!statusUpdatingOrderId}
                  >
                    <option value="">Select delivery boy</option>
                    {drivers.map((driver) => (
                      <option key={driver._id} value={driver.name}>{driver.name}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            <div className="orderlist-confirm-actions">
              <button
                type="button"
                className="orderlist-cancel-btn"
                onClick={() => setListingShippedModal({ isOpen: false, order: null, shipmentNumber: '', shipmentPartner: '' })}
                disabled={!!statusUpdatingOrderId}
              >
                Cancel
              </button>
              <button
                type="button"
                className="orderlist-submit-btn"
                onClick={handleListingShippedSubmit}
                disabled={!!statusUpdatingOrderId}
              >
                {statusUpdatingOrderId ? 'Processing...' : 'Confirm Shipped'}
              </button>
            </div>
          </div>
        </div>
      )}

      {listingStatusConfirm.isOpen && listingStatusConfirm.order && (
        <div
          className="orderlist-confirm-overlay"
          onClick={(e) => {
            e.stopPropagation();
            if (!statusUpdatingOrderId) {
              setListingStatusConfirm({
                isOpen: false,
                order: null,
                status: null,
                title: '',
                message: '',
                reason: '',
              });
            }
          }}
        >
          <div className="orderlist-confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="orderlist-confirm-header">
              <h3>{listingStatusConfirm.title}</h3>
              <button
                type="button"
                className="orderlist-confirm-close-btn"
                onClick={() => setListingStatusConfirm({
                  isOpen: false,
                  order: null,
                  status: null,
                  title: '',
                  message: '',
                  reason: '',
                })}
                disabled={!!statusUpdatingOrderId}
              >
                <X size={18} />
              </button>
            </div>
            <p>{listingStatusConfirm.message}</p>
            {requiresStatusReason(listingStatusConfirm.status) && (
              <div className="orderlist-form-group">
                <label htmlFor="listing-status-reason">
                  {getStatusReasonField(listingStatusConfirm.status) === 'returnReason' ? 'Return reason (optional)' : 'Cancel reason (optional)'}
                </label>
                <textarea
                  id="listing-status-reason"
                  value={listingStatusConfirm.reason}
                  onChange={(e) => setListingStatusConfirm((prev) => ({ ...prev, reason: e.target.value }))}
                  rows={3}
                  placeholder="Enter a reason"
                  disabled={!!statusUpdatingOrderId}
                />
              </div>
            )}
            <div className="orderlist-confirm-actions">
              <button
                type="button"
                className="orderlist-cancel-btn"
                onClick={() => setListingStatusConfirm({
                  isOpen: false,
                  order: null,
                  status: null,
                  title: '',
                  message: '',
                  reason: '',
                })}
                disabled={!!statusUpdatingOrderId}
              >
                Cancel
              </button>
              <button
                type="button"
                className="orderlist-submit-btn"
                onClick={() => handleListingStatusAdvance(
                  listingStatusConfirm.order,
                  listingStatusConfirm.status,
                  true,
                  listingStatusConfirm.reason
                )}
                disabled={!!statusUpdatingOrderId}
              >
                {statusUpdatingOrderId ? 'Processing...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

      {commentsModal.isOpen && (
        <div
          className="orderlist-confirm-overlay"
          onClick={(e) => {
            e.stopPropagation();
            handleCloseCommentsModal();
          }}
        >
          <div className="orderlist-confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="orderlist-confirm-header">
              <h3>Add/Edit Comment</h3>
              <button
                type="button"
                className="orderlist-confirm-close-btn"
                onClick={handleCloseCommentsModal}
              >
                <X size={18} />
              </button>
            </div>
            <div className="orderlist-form-group" style={{ marginTop: '15px' }}>
              <label htmlFor="modal-comments">Comment</label>
              <textarea
                id="modal-comments"
                value={commentsModal.comments}
                onChange={(e) => setCommentsModal((prev) => ({ ...prev, comments: e.target.value }))}
                rows={4}
                placeholder="Enter comments here"
                style={{ width: '100%', padding: '8px', boxSizing: 'border-box', border: '1px solid #ccc', borderRadius: '4px', marginTop: '5px' }}
              />
            </div>
            <div className="orderlist-confirm-actions" style={{ marginTop: '15px' }}>
              <button
                type="button"
                className="orderlist-cancel-btn"
                onClick={handleCloseCommentsModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className="orderlist-submit-btn"
                onClick={handleSaveComments}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Orders;
